// Lightweight .ics (iCalendar) generator. Produces a Blob and triggers download.

import { wallClockParts, toCompactLocal } from "./calendarLinks";

export interface IcsEvent {
  uid: string;
  title: string;
  description?: string;
  location?: string;
  // ISO date strings, supports all-day if hour omitted.
  start: string; // YYYY-MM-DD or YYYY-MM-DDTHH:mm
  end?: string;  // optional, defaults to start + 3h for timed events, +1d for all-day
}

// PR-E32: DTSTART/DTEND are emitted as FLOATING LOCAL times (YYYYMMDDTHHmmSS, no
// trailing Z) so a Pokemon GO event stays at its source wall-clock time in the
// user's calendar regardless of device timezone. The previous toUtc appended Z
// and read Date#getUTC*, which on a non-UTC device shifted the hour (and date).
// The wall-clock numbers are pulled out timezone-independently by wallClockParts
// (shared with calendarLinks). DTSTAMP is unaffected and stays a real UTC instant
// below, since it is the genuine "this file was generated at" moment.
function toFloatingLocal(dt: string): string {
  return toCompactLocal(wallClockParts(dt));
}

// A real UTC instant for DTSTAMP (file creation time), kept as YYYYMMDDTHHmmSSZ.
function nowUtcStamp(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    d.getUTCFullYear().toString() +
    pad(d.getUTCMonth() + 1) +
    pad(d.getUTCDate()) +
    "T" +
    pad(d.getUTCHours()) +
    pad(d.getUTCMinutes()) +
    pad(d.getUTCSeconds()) +
    "Z"
  );
}

function escape(text: string): string {
  return text
    .replace(/\\/g, "\\\\")
    .replace(/\n/g, "\\n")
    .replace(/,/g, "\\,")
    .replace(/;/g, "\\;");
}

export function buildIcs(events: IcsEvent[]): string {
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Nexus Dex//Calendar//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
  ];
  const stamp = nowUtcStamp();
  for (const ev of events) {
    const dtStart = toFloatingLocal(ev.start);
    const endRaw = ev.end ?? ev.start;
    const dtEnd = ev.end
      ? toFloatingLocal(ev.end)
      : ev.start.length <= 10
      ? toFloatingLocal(endRaw) // all-day single ~3h block
      : toFloatingLocal(endRaw);
    lines.push(
      "BEGIN:VEVENT",
      `UID:${ev.uid}@nexusdex.ai`,
      `DTSTAMP:${stamp}`,
      `DTSTART:${dtStart}`,
      `DTEND:${dtEnd}`,
      `SUMMARY:${escape(ev.title)}`,
      ev.description ? `DESCRIPTION:${escape(ev.description)}` : "",
      ev.location ? `LOCATION:${escape(ev.location)}` : "",
      "END:VEVENT",
    );
  }
  lines.push("END:VCALENDAR");
  return lines.filter(Boolean).join("\r\n");
}

export function downloadIcs(filename: string, events: IcsEvent[]) {
  const ics = buildIcs(events);
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename.endsWith(".ics") ? filename : `${filename}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 250);
}
